import { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { ChevronLeft, LogOut, Briefcase, Menu, X, Bell, User, Settings } from "lucide-react";
import { TiHome } from "react-icons/ti";
import { FaRegBell, FaChartLine, FaFolder, FaUsers } from "react-icons/fa6";
import { HiCurrencyDollar } from "react-icons/hi2";
import { MdEventNote } from "react-icons/md";
import NotificationBell from "../components/NotificationBell";
import { useAuth } from "../auth/AuthContext";
import { auth } from "../services/auth";
import Gate from "../auth/Gate";
import { NAV_CONFIG } from "../config/navigation";
import { USER_TYPES } from "../config/roles";

/* ── Design tokens (match Admin panel) ─────────────────────────── */
const STYLE_ID = "ceed-layout-styles";
function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

    .ceed-shell { min-height: 100vh; background: #f4f5f7; font-family: 'DM Sans', sans-serif; }

    /* Sidebar */
    .ceed-sidebar {
      position: fixed; left: 0; top: 0; height: 100%; z-index: 40;
      background: #0c1118;
      display: flex; flex-direction: column;
      transition: width 280ms cubic-bezier(.4,0,.2,1), transform 280ms cubic-bezier(.4,0,.2,1);
      overflow: hidden;
    }
    .ceed-sidebar.expanded  { width: 240px; }
    .ceed-sidebar.collapsed { width: 64px; }

    /* Mobile: sidebar is off-canvas by default */
    @media (max-width: 767px) {
      .ceed-sidebar { transform: translateX(-100%); width: 240px !important; }
      .ceed-sidebar.mobile-open { transform: translateX(0); }
    }

    /* Sidebar brand bar */
    .ceed-brand {
      height: 64px; display: flex; align-items: center; gap: 12px;
      padding: 0 14px; border-bottom: 1px solid rgba(255,255,255,.07);
      flex-shrink: 0;
    }
    .ceed-brand-logo {
      width: 36px; height: 36px; border-radius: 8px;
      background: #ffffff; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
    }
    .ceed-brand-logo span {
      font-family: 'DM Sans', sans-serif;
      font-size: 15px; color: #0c1118; letter-spacing: .04em;
    }
    .ceed-brand-text { overflow: hidden; white-space: nowrap; }
    .ceed-brand-name {
      font-family: 'DM Sans', sans-serif;
      font-size: 17px; letter-spacing: .06em; color: #fff; line-height: 1.1;
    }
    .ceed-brand-sub {
      font-family: 'DM Sans', sans-serif;
      font-size: 9px; letter-spacing: .04em;
      color: rgba(255,255,255,.45); margin-top: 1px;
    }

    /* Role chip */
    .ceed-role-chip {
      margin: 12px 14px 4px;
      padding: 6px 10px;
      background: rgba(255,255,255,.07);
      border: 1px solid rgba(255,255,255,.12);
      border-radius: 6px;
      display: flex; align-items: center; gap: 8px;
      overflow: hidden; flex-shrink: 0;
    }
    .ceed-role-dot {
      width: 7px; height: 7px; border-radius: 50%;
      background: rgba(255,255,255,.5); flex-shrink: 0;
    }
    .ceed-role-label {
      font-family: 'DM Sans', sans-serif;
      font-size: 10px; letter-spacing: .04em;
      color: rgba(255,255,255,.55); white-space: nowrap; overflow: hidden;
    }

    /* Nav section label */
    .ceed-nav-section {
      padding: 14px 14px 4px;
      font-family: 'DM Sans', sans-serif;
      font-size: 9px; letter-spacing: .04em;
      color: rgba(255,255,255,.25);
      white-space: nowrap; overflow: hidden; flex-shrink: 0;
    }

    /* Nav link */
    .ceed-nav { flex: 1; overflow-y: auto; overflow-x: hidden; padding: 4px 10px 16px; }
    .ceed-nav::-webkit-scrollbar { width: 3px; }
    .ceed-nav::-webkit-scrollbar-track { background: transparent; }
    .ceed-nav::-webkit-scrollbar-thumb { background: rgba(255,255,255,.1); border-radius: 2px; }

    .ceed-nav-link {
      display: flex; align-items: center; gap: 11px;
      padding: 9px 10px; border-radius: 7px; margin-bottom: 2px;
      color: rgba(255,255,255,.55);
      font-size: 13.5px; font-weight: 500;
      text-decoration: none; white-space: nowrap; overflow: hidden;
      transition: background 180ms, color 180ms;
      position: relative;
    }
    .ceed-nav-link:hover {
      background: rgba(255,255,255,.07);
      color: rgba(255,255,255,.9);
    }
    .ceed-nav-link.active {
      background: rgba(255,255,255,.08);
      color: #ffffff;
    }
    .ceed-nav-link.active::before {
      content: '';
      position: absolute; left: 0; top: 20%; bottom: 20%;
      width: 3px; border-radius: 0 3px 3px 0;
      background: #ffffff;
    }
    .ceed-nav-icon { flex-shrink: 0; width: 18px; display: flex; align-items: center; justify-content: center; }
    .ceed-nav-label { overflow: hidden; }

    /* Collapse toggle */
    .ceed-collapse-btn {
      margin: 10px; padding: 8px; border-radius: 7px;
      background: rgba(255,255,255,.05);
      border: 1px solid rgba(255,255,255,.07);
      color: rgba(255,255,255,.4);
      cursor: pointer; display: flex; align-items: center; justify-content: center;
      transition: background 180ms, color 180ms;
      flex-shrink: 0;
    }
    .ceed-collapse-btn:hover { background: rgba(255,255,255,.1); color: rgba(255,255,255,.8); }

    /* Main area */
    .ceed-main { transition: margin-left 280ms cubic-bezier(.4,0,.2,1); min-height: 100vh; }
    .ceed-main.expanded  { margin-left: 240px; }
    .ceed-main.collapsed { margin-left: 64px; }
    @media (max-width: 767px) {
      .ceed-main.expanded, .ceed-main.collapsed { margin-left: 0 !important; }
    }

    /* Topbar */
    .ceed-topbar {
      position: fixed; top: 0; right: 0; height: 64px; z-index: 30;
      background: rgba(255,255,255,.95); backdrop-filter: blur(12px);
      border-bottom: 1px solid #e8ecf0;
      display: flex; align-items: center;
      padding: 0 20px; gap: 12px;
      transition: left 280ms cubic-bezier(.4,0,.2,1);
    }
    .ceed-topbar.expanded  { left: 240px; }
    .ceed-topbar.collapsed { left: 64px; }
    @media (max-width: 767px) {
      .ceed-topbar { left: 0 !important; }
    }

    .ceed-topbar-title {
      font-family: 'DM Sans', sans-serif;
      font-size: 15px; font-weight: 600; color: #0c1118; line-height: 1.2;
    }
    .ceed-topbar-sub {
      font-family: 'DM Sans', sans-serif;
      font-size: 10px; color: #9ca3af; letter-spacing: .06em;
    }
    .ceed-topbar-spacer { flex: 1; }

    /* Mobile overlay */
    .ceed-overlay {
      display: none; position: fixed; inset: 0; background: rgba(0,0,0,.55);
      z-index: 35; backdrop-filter: blur(2px);
    }
    @media (max-width: 767px) {
      .ceed-overlay.visible { display: block; }
    }

    /* Profile button */
    .ceed-profile-btn {
      display: flex; align-items: center; gap: 9px;
      padding: 6px 10px 6px 8px;
      border: 1px solid #e8ecf0; border-radius: 8px;
      background: #fff; cursor: pointer;
      transition: box-shadow 180ms, border-color 180ms;
    }
    .ceed-profile-btn:hover { box-shadow: 0 2px 8px rgba(0,0,0,.08); border-color: #d1d5db; }
    .ceed-profile-avatar {
      width: 32px; height: 32px; border-radius: 50%;
      object-fit: cover; border: 2px solid #e2e8f0;
    }
    .ceed-profile-name { font-size: 13px; font-weight: 600; color: #0c1118; }
    .ceed-profile-role {
      font-family: 'DM Sans', sans-serif;
      font-size: 10px; color: #6b7280; letter-spacing: .06em;
    }

    /* Profile dropdown */
    .ceed-dropdown {
      position: absolute; right: 0; top: calc(100% + 8px);
      width: 240px; background: #fff;
      border: 1px solid #e8ecf0; border-radius: 10px;
      box-shadow: 0 8px 28px rgba(0,0,0,.12);
      overflow: hidden; z-index: 50;
    }
    .ceed-dropdown-header {
      padding: 14px 16px; background: #0c1118;
      display: flex; align-items: center; gap: 10px;
    }
    .ceed-dropdown-avatar {
      width: 40px; height: 40px; border-radius: 50%;
      object-fit: cover; border: 2px solid rgba(255,255,255,.2);
    }
    .ceed-dropdown-name { font-size: 14px; font-weight: 600; color: #fff; }
    .ceed-dropdown-email { font-size: 11px; color: rgba(255,255,255,.5); margin-top: 1px; }
    .ceed-dropdown-badge {
      font-family: 'DM Sans', sans-serif;
      font-size: 9px; letter-spacing: .1em;
      background: rgba(255,255,255,.12); color: rgba(255,255,255,.7);
      border: 1px solid rgba(255,255,255,.2);
      padding: 2px 7px; border-radius: 4px; margin-top: 3px; display: inline-block;
    }
    .ceed-dropdown-item {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 16px;
      font-size: 13.5px; color: #374151;
      text-decoration: none; cursor: pointer;
      transition: background 150ms;
    }
    .ceed-dropdown-item:hover { background: #f9fafb; }
    .ceed-dropdown-item.danger { color: #dc2626; }
    .ceed-dropdown-item.danger:hover { background: #fef2f2; }
    .ceed-dropdown-divider { height: 1px; background: #f3f4f6; margin: 2px 0; }

    /* Page content */
    .ceed-page { padding-top: 64px; min-height: 100vh; }
    .ceed-page-inner { padding: 24px; }
    @media (max-width: 767px) { .ceed-page-inner { padding: 16px; } }

    /* Hide profile names on mobile */
    @media (max-width: 767px) {
      .ceed-profile-names { display: none !important; }
      .ceed-profile-chevron { display: none !important; }
      .ceed-collapse-btn { display: none !important; }
    }

    /* Sidebar sign-out button */
    .ceed-signout-btn {
      margin: 0 10px 10px; padding: 9px 10px; border-radius: 7px;
      background: none; border: none; cursor: pointer;
      display: flex; align-items: center; gap: 10px;
      color: rgba(255,255,255,.4); font-size: 13.5px; font-weight: 500;
      font-family: 'DM Sans', sans-serif;
      transition: background 180ms, color 180ms; flex-shrink: 0;
      text-align: left; width: calc(100% - 20px);
    }
    .ceed-signout-btn:hover { background: rgba(239,68,68,.15); color: #f87171; }
    .ceed-signout-icon { flex-shrink: 0; width: 18px; display: flex; align-items: center; justify-content: center; }
  `;
  document.head.appendChild(el);
}

function iconFor(path) {
  if (path === "/ceed") return <TiHome size={18} />;
  if (path.includes("/projects")) return <FaFolder size={16} />;
  if (path.includes("/notifications")) return <FaRegBell size={16} />;
  if (path.includes("/purchase-plans")) return <HiCurrencyDollar size={18} />;
  if (path.includes("/reports")) return <FaChartLine size={16} />;
  if (path.includes("/quotes")) return <MdEventNote size={17} />;
  if (path.includes("/users")) return <FaUsers size={16} />;
  return <TiHome size={18} />;
}

export default function CeedLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 767);
  const location = useLocation();

  useEffect(() => { injectStyles(); }, []);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  // On mobile always show expanded sidebar content
  const showExpanded = !collapsed || isMobile;

  return (
    <div className="ceed-shell">
      {/* Mobile overlay */}
      <div
        className={`ceed-overlay ${mobileOpen ? "visible" : ""}`}
        onClick={() => setMobileOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`ceed-sidebar ${collapsed ? "collapsed" : "expanded"} ${mobileOpen ? "mobile-open" : ""}`}>
        {/* Brand */}
        <div className="ceed-brand">
          <div className="ceed-brand-logo">
            <span>MD</span>
          </div>
          {showExpanded && (
            <div className="ceed-brand-text">
              <div className="ceed-brand-name">MyDesignerPlus</div>
              <div className="ceed-brand-sub">Business Console</div>
            </div>
          )}
        </div>

        {/* Role chip */}
        {showExpanded && (
          <div className="ceed-role-chip">
            <div className="ceed-role-dot" />
            <div className="ceed-role-label">CEED Manager</div>
          </div>
        )}

        {/* Nav section label */}
        {showExpanded && <div className="ceed-nav-section">Navigation</div>}

        {/* Nav links */}
        <nav className="ceed-nav">
          {(NAV_CONFIG[USER_TYPES.CEED] || []).map((item) => (
            <Gate key={item.to} perm={item.perm}>
              <NavLink
                to={item.to}
                end={item.to === "/ceed"}
                className={({ isActive }) =>
                  `ceed-nav-link${isActive ? " active" : ""}`
                }
                title={!showExpanded ? item.label : undefined}
              >
                <span className="ceed-nav-icon">{iconFor(item.to)}</span>
                {showExpanded && <span className="ceed-nav-label">{item.label}</span>}
              </NavLink>
            </Gate>
          ))}
        </nav>

        {/* Sign Out */}
        <CeedSidebarSignOut showExpanded={showExpanded} />

        {/* Collapse toggle — hidden on mobile via CSS */}
        <button
          className="ceed-collapse-btn"
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <ChevronLeft
            size={16}
            style={{
              transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 280ms",
            }}
          />
        </button>
      </aside>

      {/* Main */}
      <div className={`ceed-main ${collapsed ? "collapsed" : "expanded"}`}>
        {/* Topbar */}
        <CeedTopbar
          collapsed={collapsed}
          onMobileToggle={() => setMobileOpen(!mobileOpen)}
          mobileOpen={mobileOpen}
        />

        {/* Page content */}
        <div className="ceed-page">
          <div className="ceed-page-inner">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}

function CeedSidebarSignOut({ showExpanded }) {
  async function handleSignOut() {
    try { await auth.logout(); } catch {}
    window.location.replace("/login");
  }
  return (
    <button className="ceed-signout-btn" onClick={handleSignOut} title="Sign Out">
      <span className="ceed-signout-icon"><LogOut size={16} /></span>
      {showExpanded && "Sign Out"}
    </button>
  );
}

function CeedTopbar({ collapsed, onMobileToggle, mobileOpen }) {
  return (
    <header className={`ceed-topbar ${collapsed ? "collapsed" : "expanded"}`}>
      {/* Mobile hamburger */}
      <button
        onClick={onMobileToggle}
        style={{
          display: "none",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "4px",
          color: "#374151",
        }}
        className="ceed-hamburger"
        aria-label="Toggle menu"
      >
        {mobileOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      <style>{`
        @media (max-width: 767px) {
          .ceed-hamburger { display: flex !important; }
          .ceed-topbar-titles { display: none !important; }
        }
      `}</style>

      <div className="ceed-topbar-titles">
        <div className="ceed-topbar-title">Ceed Civil Business Console</div>
        <div className="ceed-topbar-sub">MANAGER PORTAL</div>
      </div>

      <div className="ceed-topbar-spacer" />

      <NotificationBell />
      <CeedProfileMenu />
    </header>
  );
}

function CeedProfileMenu() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [me, setMe] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  useEffect(() => {
    function handleEscape(e) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [open]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
          { credentials: "include", cache: "no-store" }
        );
        if (res.ok) {
          const data = await res.json();
          if (!ignore) setMe(data.safeUser);
        }
      } catch {}
    })();
    return () => { ignore = true; };
  }, []);

  const displayName =
    me?.firstname || me?.lastname
      ? `${me?.firstname || ""} ${me?.lastname || ""}`.trim()
      : me?.email || user?.firstname || "Manager";

  const avatarSrc = me?.avatarUrl || "/assets/profile.png";

  async function handleSignOut() {
    setOpen(false);
    try { await auth.logout(); } catch {}
    window.location.replace("/login");
  }

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button className="ceed-profile-btn" onClick={() => setOpen(!open)}>
        <img src={avatarSrc} alt="Profile" className="ceed-profile-avatar" />
        <div style={{ display: "flex", flexDirection: "column", textAlign: "left" }} className="ceed-profile-names">
          <span className="ceed-profile-name">{displayName}</span>
          <span className="ceed-profile-role">CEED MGR</span>
        </div>
        <ChevronLeft
          size={14}
          className="ceed-profile-chevron"
          style={{
            color: "#9ca3af",
            transform: open ? "rotate(90deg)" : "rotate(-90deg)",
            transition: "transform 200ms",
          }}
        />
      </button>

      {open && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 45 }}
            onClick={() => setOpen(false)}
          />
          <div className="ceed-dropdown" style={{ zIndex: 50 }}>
            {/* Header */}
            <div className="ceed-dropdown-header">
              <img src={avatarSrc} alt="" className="ceed-dropdown-avatar" />
              <div>
                <div className="ceed-dropdown-name">{displayName}</div>
                <div className="ceed-dropdown-email">{me?.email}</div>
                <div className="ceed-dropdown-badge">CEED MANAGER</div>
              </div>
            </div>

            {/* Links */}
            <div style={{ padding: "6px 0" }}>
              <Link
                to="/ceed/profile"
                className="ceed-dropdown-item"
                onClick={() => setOpen(false)}
              >
                <User size={15} />
                My Profile
              </Link>
              <Link
                to="/ceed/notifications"
                className="ceed-dropdown-item"
                onClick={() => setOpen(false)}
              >
                <Bell size={15} />
                Notifications
              </Link>
            </div>

            <div className="ceed-dropdown-divider" />

            <div style={{ padding: "6px 0" }}>
              <button
                onClick={handleSignOut}
                className="ceed-dropdown-item danger"
                style={{ width: "100%", background: "none", border: "none", textAlign: "left" }}
              >
                <LogOut size={15} />
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
